import { Navbar } from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Target, TrendingUp, Users, Award, Calendar, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";

const Achievements = () => {
  const { t, language } = useLanguage();

  // Translated stats data
  const stats = [
    { icon: TrendingUp, value: "100M+", label: t('achievements.stats.funding') },
    { icon: Target, value: "50+", label: t('achievements.stats.projects') },
    { icon: Users, value: "200+", label: t('achievements.stats.investors') },
    { icon: Award, value: "92%", label: t('achievements.stats.successRate') },
  ];

  // Translated featured projects data
  const featuredProjects = [
    {
      id: 1,
      title: t('achievements.featured.projectsList.0.title'),
      category: t('achievements.featured.projectsList.0.category'),
      funding: t('achievements.featured.projectsList.0.funding'),
      roi: t('achievements.featured.projectsList.0.roi'),
      description: t('achievements.featured.projectsList.0.description'),
    },
    {
      id: 2,
      title: t('achievements.featured.projectsList.1.title'),
      category: t('achievements.featured.projectsList.1.category'),
      funding: t('achievements.featured.projectsList.1.funding'),
      roi: t('achievements.featured.projectsList.1.roi'),
      description: t('achievements.featured.projectsList.1.description'),
    },
    {
      id: 3,
      title: t('achievements.featured.projectsList.2.title'),
      category: t('achievements.featured.projectsList.2.category'),
      funding: t('achievements.featured.projectsList.2.funding'),
      roi: t('achievements.featured.projectsList.2.roi'),
      description: t('achievements.featured.projectsList.2.description'),
    },
  ];

  // Translated milestones data
  const milestones = [
    { year: t('achievements.milestones.milestonesList.0.year'), title: t('achievements.milestones.milestonesList.0.title'), desc: t('achievements.milestones.milestonesList.0.desc') },
    { year: t('achievements.milestones.milestonesList.1.year'), title: t('achievements.milestones.milestonesList.1.title'), desc: t('achievements.milestones.milestonesList.1.desc') },
    { year: t('achievements.milestones.milestonesList.2.year'), title: t('achievements.milestones.milestonesList.2.title'), desc: t('achievements.milestones.milestonesList.2.desc') },
    { year: t('achievements.milestones.milestonesList.3.year'), title: t('achievements.milestones.milestonesList.3.title'), desc: t('achievements.milestones.milestonesList.3.desc') },
  ];

  return (
    <div className="min-h-screen" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="py-20 bg-hero-gradient text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <span className="text-sm font-semibold text-primary-foreground uppercase tracking-wide animate-fade-in-down" style={{ animationDelay: '0.1s' }}>
                {t('achievements.hero.sectionTitle')}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mt-3 mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                {t('achievements.hero.title')}
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                {t('achievements.hero.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <stat.icon className="w-8 h-8 text-primary" />
                  </div>
                  <span className="text-3xl font-bold block mb-1">{stat.value}</span>
                  <span className="text-muted-foreground">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('achievements.featured.title')}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t('achievements.featured.subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {featuredProjects.map((project) => (
                <div key={project.id} className="bg-card rounded-2xl p-6 shadow-card hover:shadow-elevated transition-all duration-300">
                  <div className="flex items-start justify-between mb-4">
                    <span className="text-sm text-muted-foreground">{project.category}</span>
                    <span className="text-sm font-semibold text-secondary">{project.roi} ROI</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{project.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{project.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-primary font-semibold">{project.funding}</span>
                    <Button variant="outline" size="sm">{t('common.details')}</Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Button asChild size="lg" className="gap-2 bg-hero-gradient hover:opacity-90 text-primary-foreground">
                <Link to="/projects">
                  {t('achievements.featured.viewAll')}
                  <ArrowLeft className="w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-20 bg-sidebar text-sidebar-foreground">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('achievements.milestones.title')}</h2>
              <p className="text-sidebar-foreground/70 max-w-2xl mx-auto">
                {t('achievements.milestones.subtitle')}
              </p>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute right-4 md:right-1/2 top-0 bottom-0 w-0.5 bg-sidebar-primary/30 transform md:translate-x-1/2"></div>

              <div className="space-y-12">
                {milestones.map((milestone, index) => (
                  <div 
                    key={milestone.year} 
                    className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} md:justify-between`}
                  >
                    {/* Year */}
                    <div className="md:w-5/12 mb-4 md:mb-0">
                      <div className="text-right md:text-left">
                        <span className="inline-block px-4 py-2 rounded-full bg-sidebar-primary text-sidebar-primary-foreground font-semibold">
                          {milestone.year}
                        </span>
                      </div>
                    </div>

                    {/* Dot */}
                    <div className="absolute right-0 md:right-1/2 w-8 h-8 rounded-full bg-sidebar-primary border-4 border-sidebar flex items-center justify-center transform md:translate-x-1/2">
                      <Calendar className="w-4 h-4 text-sidebar-primary-foreground" />
                    </div>

                    {/* Content */}
                    <div className="md:w-5/12 mt-4 md:mt-0">
                      <div className="bg-sidebar-accent rounded-2xl p-6">
                        <h3 className="font-bold text-lg mb-2">{milestone.title}</h3>
                        <p className="text-sidebar-foreground/70">{milestone.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Achievements;